import React from 'react';
import { flushSync } from 'react-dom';

import {
  clampPanelWidth,
  DEFAULT_ARTIFACT_PANEL_WIDTH,
  outlineRailFits,
  panelWidthBounds,
  readPanelWidthPreference,
  writePanelWidthPreference
} from '~/components/workspaces/lib/artifact-panel-width';

const useElementWidth = (ref: React.RefObject<HTMLElement | null>) => {
  const [width, setWidth] = React.useState(0);
  React.useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return;
    setWidth(element.clientWidth);
    if (typeof ResizeObserver === 'undefined') return;
    // Rendering inside the callback, before the browser paints, keeps the panel's
    // clamp in the same frame as the resize; a scheduled render lands a frame
    // later, after the conversation has been painted under its minimum.
    const observer = new ResizeObserver(([entry]) =>
      flushSync(() => setWidth(entry.contentRect.width))
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, [ref]);
  return width;
};

/**
 * The artifact panel's width within the workspace row that `rowRef` points at.
 *
 * A drag reports through `preview` on every move and through `commit` once on
 * release, even when it ends where it began. Only `commit` saves the width or
 * re-renders with it, so the viewer is not re-rendered per pixel; `preview`
 * re-renders only when the move changes whether the outline rail fits.
 */
export const useArtifactPanelWidth = (rowRef: React.RefObject<HTMLElement | null>) => {
  const rowWidth = useElementWidth(rowRef);
  const [preference, setPreference] = React.useState(readPanelWidthPreference);
  const [previewRailFits, setPreviewRailFits] = React.useState<boolean | null>(null);

  const bounds = panelWidthBounds(rowWidth);
  const width = clampPanelWidth(preference, bounds);

  const preview = React.useCallback(
    (next: number) => setPreviewRailFits(outlineRailFits(rowWidth, next)),
    [rowWidth]
  );

  // Ending a drag where it began saves nothing, so a click on the handle does not
  // replace a preference that is only clamped by the current window.
  const commit = React.useCallback(
    (next: number) => {
      setPreviewRailFits(null);
      if (next === width) return;
      setPreference(next);
      writePanelWidthPreference(next);
    },
    [width]
  );

  const reset = React.useCallback(() => {
    setPreference(DEFAULT_ARTIFACT_PANEL_WIDTH);
    writePanelWidthPreference(DEFAULT_ARTIFACT_PANEL_WIDTH);
  }, []);

  return {
    width,
    bounds,
    railFits: previewRailFits ?? outlineRailFits(rowWidth, width),
    preview,
    commit,
    reset
  };
};
